import React, { useEffect, useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import { ISelection } from "src/constants/interfaces/bet";
import { asyncWrapper } from "src/hocs/asyncWrapper";
import selectionsStore, { storeChangeSelection, storeChangeAllSelectionsSingleStake } from "src/stores/selections";
import mainStore, { storeSetSuperbet } from "src/stores/main";
import EmptyState from "../emptyState";
import Selection from "./components/selection";
import BetBuilderGroup from "./components/betBuilderGroup";
import { decimalString, MainBetType } from "src/constants/types";
import { useConfig } from "src/hooks/config";
import { useBetBuilderValidation } from "src/hooks/betBuilderValidation";
import { usePrevious } from "src/hooks/previous";

import "./style.scss";

interface IProps {
	onChange(selections: ISelection[]): void;
}

// Split selections into bet builder groups (one per game) and regular singles.
// Bet builder groups render as a single combined card above the regular list.
const partitionSelections = (selections: ISelection[]) => {
	const betBuilderGroups: Record<number, ISelection[]> = {};
	const regular: ISelection[] = [];

	selections.forEach((selection) => {
		if (selection.event.isInternalBetBuilder) {
			const gameId = selection.game.id;
			if (!betBuilderGroups[gameId]) betBuilderGroups[gameId] = [];
			betBuilderGroups[gameId].push(selection);
		} else {
			regular.push(selection);
		}
	});

	return { betBuilderGroups, regular };
};

const SelectionsList = ({ onChange }: IProps) => {
	const config = useConfig();
	const { betType, maxStake } = useRecoilValue(mainStore);
	const { selections } = useRecoilValue(selectionsStore);
	const selectionBlockref = useRef<null | HTMLDivElement>(null);
	const prevSelections = usePrevious(selections);

	// Keep bet builder combined odds in sync with the API on every selection change.
	useBetBuilderValidation(onChange);

	const { betBuilderGroups, regular } = useMemo(
		() => partitionSelections(selections),
		[selections]
	);

	useEffect(() => {
		if (prevSelections && prevSelections?.length < selections.length)
			selectionBlockref.current?.lastElementChild?.scrollIntoView(true);
	}, [selections, prevSelections]);

	const onSelectionRemove = (eventId: number) => {
		const newSelections = selections.filter(item => item.event.id !== eventId);
		onChange(newSelections);
	};

	const onGroupRemove = (eventIds: number[]) => {
		const removeSet = new Set(eventIds);
		const newSelections = selections.filter(item => !removeSet.has(item.event.id));
		onChange(newSelections);
	};

	const onSelectionChange = (oldEventId: number, newSelection: ISelection) => {
		const newSelections = selections.map(item => (item.event.id === oldEventId ? newSelection : item));
		onChange(newSelections);
	};

	const onStakeChange = (selection: ISelection, value: decimalString) => {
		storeChangeSelection(selection.event.id, {
			...selection,
			singleStake: value,
		});
	};

	const onStakeApplyToAll = (value: decimalString) => storeChangeAllSelectionsSingleStake(value);

	useEffect(() => {
		if (maxStake && config.enableAutomaticSuperbet) {
			const exceededMaxStakeLimit = selections.some(item => +(item.singleStake || "") > maxStake);
			storeSetSuperbet(exceededMaxStakeLimit);
		}
	}, [config, maxStake, selections]);

	if (!selections.length) return <EmptyState betType={betType as MainBetType} />;

	return (
		<div className="B-betslip-selections-list" ref={selectionBlockref}>
			{Object.entries(betBuilderGroups).map(([gameId, groupSelections]) => (
				<BetBuilderGroup
					key={`bet-builder-${gameId}`}
					gameId={+gameId}
					selections={groupSelections}
					onRemoveSelection={onSelectionRemove}
					onRemoveGroup={onGroupRemove}
				/>
			))}
			{regular.map(item => (
				<Selection
					key={item.event.id}
					selection={item}
					onRemove={onSelectionRemove}
					onChange={(newSelection: ISelection) =>
						onSelectionChange(item.event.id, newSelection)
					}
					onStakeChange={value => onStakeChange(item, value)}
					onStakeApplyToAll={onStakeApplyToAll}
				/>
			))}
		</div>
	);
};

export default asyncWrapper<IProps>(SelectionsList);
